/**
 * @module public/js/modals/followUpEnrollmentModal
 * @summary Offers an opt-in next-sequence enrollment after a terminal outcome.
 */

import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';

export const FOLLOW_UP_ENROLLMENT_TRIGGERS = Object.freeze({
  GO: 'GO',
  NO_GO: 'NO_GO',
  SEQUENCE_COMPLETE: 'SEQUENCE_COMPLETE',
});

const MODAL_ID = 'followUpEnrollmentModal';
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Escape a value before placing it into modal HTML.
 *
 * @param {unknown} value - Untrusted display value.
 * @returns {string} HTML-safe text.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Normalize the playbook-level follow-up enrollment setting.
 *
 * @param {unknown} value - Stored Playbook setting.
 * @returns {{enabled:boolean}} Canonical setting.
 */
export function normalizeFollowUpEnrollmentConfig(value) {
  return {
    enabled: value === true || value?.enabled === true,
  };
}

/**
 * Resolve whether a playbook enables the terminal enrollment prompt.
 *
 * @param {Record<string, unknown>|null|undefined} playbook - Playbook data.
 * @returns {boolean} True when the prompt is enabled.
 */
export function isFollowUpEnrollmentEnabled(playbook) {
  return normalizeFollowUpEnrollmentConfig(playbook?.followUpEnrollment)
    .enabled;
}

/**
 * Check whether a seller can select a destination playbook.
 *
 * @param {Record<string, unknown>} playbook - Destination playbook data.
 * @param {string} userId - Signed-in seller uid.
 * @returns {boolean} True when the playbook is assigned to the seller.
 */
export function isFollowUpDestinationEligible(playbook, userId) {
  const sellerId = String(userId || '').trim();
  if (!sellerId || !playbook || typeof playbook !== 'object') return false;
  const allowedSellers = Array.isArray(playbook.allowedSellers)
    ? playbook.allowedSellers.map((value) => String(value || '').trim())
    : [];
  return allowedSellers.includes(sellerId);
}

/**
 * Determine whether the current step is the final configured playbook step.
 *
 * @param {Record<string, unknown>|Array<unknown>} steps - Playbook steps.
 * @param {unknown} currentStepIndex - Active step index.
 * @returns {boolean} True when no later step exists.
 */
export function isSequenceFinalStep(steps, currentStepIndex) {
  const current = Number(currentStepIndex);
  if (!Number.isFinite(current) || !steps || typeof steps !== 'object') {
    return false;
  }
  const indices = Object.keys(steps).map(Number).filter(Number.isFinite);
  return indices.length > 0 && current >= Math.max(...indices);
}

/**
 * Resolve the terminal moment that may offer another sequence.
 *
 * @param {object} params - Outcome context.
 * @param {string} params.outcome - Recorded outcome.
 * @param {Record<string, unknown>} [params.metadata={}] - Outcome metadata.
 * @param {unknown} params.currentStepIndex - Active step index.
 * @param {Record<string, unknown>|Array<unknown>} params.steps - Playbook steps.
 * @returns {'GO'|'NO_GO'|'SEQUENCE_COMPLETE'|null} Eligible trigger.
 */
export function resolveFollowUpEnrollmentTrigger({
  outcome,
  metadata = {},
  currentStepIndex,
  steps,
} = {}) {
  const normalizedOutcome = String(outcome || '')
    .trim()
    .toUpperCase();
  if (normalizedOutcome === 'GO') return FOLLOW_UP_ENROLLMENT_TRIGGERS.GO;
  if (normalizedOutcome === 'NO_GO') {
    const hasResumeDate =
      typeof metadata?.nextTouchDate === 'string' &&
      ISO_DATE_PATTERN.test(metadata.nextTouchDate);
    return hasResumeDate ? null : FOLLOW_UP_ENROLLMENT_TRIGGERS.NO_GO;
  }
  if (isSequenceFinalStep(steps, currentStepIndex)) {
    return FOLLOW_UP_ENROLLMENT_TRIGGERS.SEQUENCE_COMPLETE;
  }
  return null;
}

/**
 * Return a local yyyy-mm-dd key for the enrollment date input.
 *
 * @param {Date} [date=new Date()] - Local date source.
 * @returns {string} Local date key.
 */
export function localDateKey(date = new Date()) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * Open the next-sequence and start-date picker.
 *
 * @param {object} params - Modal inputs.
 * @param {Array<Record<string, unknown>>} params.playbooks - Eligible destinations.
 * @param {'GO'|'NO_GO'|'SEQUENCE_COMPLETE'} params.trigger - Terminal trigger.
 * @param {{register:Function,open:Function,close:Function}} params.modalService - Modal controller.
 * @param {string} [params.minimumDate] - Earliest selectable yyyy-mm-dd date.
 * @param {boolean} [params.enableTooltips] - Tooltip feature switch.
 * @returns {Promise<{playbookId:string,startISO:string,trigger:string}|null>} Selection or null when dismissed.
 */
export async function openFollowUpEnrollmentModal({
  playbooks = [],
  trigger,
  modalService,
  minimumDate = localDateKey(),
  enableTooltips,
} = {}) {
  if (!modalService?.register || !modalService?.open || !modalService?.close) {
    throw new Error('Follow-up enrollment modal service unavailable.');
  }
  const options = playbooks
    .map((playbook) => ({
      id: String(playbook?.id || '').trim(),
      label: String(
        playbook?.displayName || playbook?.name || playbook?.id || ''
      ).trim(),
    }))
    .filter((playbook) => playbook.id && playbook.label)
    .sort((a, b) => a.label.localeCompare(b.label));
  if (!options.length) return null;

  let host = document.getElementById(MODAL_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = MODAL_ID;
    host.className = 'hidden modal-overlay';
    document.body.appendChild(host);
    modalService.register(MODAL_ID, host);
  }

  const triggerCopy = {
    GO: 'This GO will end the current sequence. Choose what should happen next.',
    NO_GO:
      'This NO GO will end the current sequence. Choose when this person should enter a new one.',
    SEQUENCE_COMPLETE:
      'This sequence is complete. Choose when this person should start another one.',
  };

  host.innerHTML = /* html */ `
    <div class="modal-container modal-grid modal-auto modal-teal p-6 focus-choice-modal focus-follow-up-enrollment">
      <header class="focus-modal__header mb-4">
        <div class="focus-modal__title-group">
          <h3>Enroll in another sequence?</h3>
          <p>${escapeHtml(triggerCopy[trigger] || triggerCopy.SEQUENCE_COMPLETE)}</p>
        </div>
      </header>
      <form id="followUpEnrollmentForm" class="focus-choice-modal__panel focus-follow-up-enrollment__form">
        <label class="focus-follow-up-enrollment__field">
          <span>Sequence</span>
          <select name="playbookId" class="form-select" required>
            <option value="">Select a sequence</option>
            ${options
              .map(
                (playbook) =>
                  `<option value="${escapeHtml(playbook.id)}">${escapeHtml(playbook.label)}</option>`
              )
              .join('')}
          </select>
        </label>
        <label class="focus-follow-up-enrollment__field">
          <span>Start date</span>
          <input
            name="startISO"
            class="form-control"
            type="date"
            min="${escapeHtml(minimumDate)}"
            value="${escapeHtml(minimumDate)}"
            required
          />
        </label>
        <p class="focus-follow-up-enrollment__help">
          The person will be queued now and become eligible for the selected sequence on this date.
        </p>
      </form>
      <footer class="focus-modal__footer focus-choice-modal__footer">
        <div class="focus-modal__footer-actions" data-actions="2">
          <button
            id="followUpEnrollmentCancel"
            type="button"
            class="btn-pill btn--grey btn--info-sm btn--secondary-action"
            data-focus-tooltip="Finish without scheduling another sequence."
          >
            Not now
          </button>
          <button
            id="followUpEnrollmentSave"
            type="button"
            class="btn-pill btn--orange btn--info-sm btn--primary-action"
            data-focus-tooltip="Queue this person for the selected sequence and date."
            disabled
          >
            Enroll again
          </button>
        </div>
      </footer>
    </div>`;

  applyFocusModeTooltips(host, {
    enableTooltips:
      enableTooltips ??
      document.body?.getAttribute('data-focus-tooltips') !== 'false',
  });

  return new Promise((resolve) => {
    const form = host.querySelector('#followUpEnrollmentForm');
    const sequenceInput = form.querySelector('select[name="playbookId"]');
    const dateInput = form.querySelector('input[name="startISO"]');
    const cancelButton = host.querySelector('#followUpEnrollmentCancel');
    const saveButton = host.querySelector('#followUpEnrollmentSave');
    let settled = false;

    /** Update the primary action from the current field validity. */
    const syncValidity = () => {
      saveButton.disabled = !(
        sequenceInput.value &&
        ISO_DATE_PATTERN.test(dateInput.value) &&
        dateInput.value >= minimumDate
      );
    };

    /** Close the modal, remove listeners, and resolve once. */
    const settle = (value) => {
      if (settled) return;
      settled = true;
      modalService.close(MODAL_ID);
      sequenceInput.removeEventListener('change', syncValidity);
      dateInput.removeEventListener('input', syncValidity);
      cancelButton.removeEventListener('click', onCancel);
      saveButton.removeEventListener('click', onSave);
      document.removeEventListener('keydown', onKeyDown);
      resolve(value);
    };

    /** Dismiss without scheduling. */
    const onCancel = () => settle(null);

    /** Resolve the validated destination selection. */
    const onSave = () => {
      syncValidity();
      if (saveButton.disabled) return;
      settle({
        playbookId: sequenceInput.value,
        startISO: dateInput.value,
        trigger,
      });
    };

    /** Treat Escape as the modal's single dismiss action. */
    const onKeyDown = (event) => {
      if (event.key === 'Escape') settle(null);
    };

    sequenceInput.addEventListener('change', syncValidity);
    dateInput.addEventListener('input', syncValidity);
    cancelButton.addEventListener('click', onCancel);
    saveButton.addEventListener('click', onSave);
    document.addEventListener('keydown', onKeyDown);
    syncValidity();
    modalService.open(MODAL_ID);
  });
}
